import api from './api';

/**
 * Souscription Web Push de l'appareil (chantier PWA-01).
 *
 * Complète le SSE sans le remplacer : le flux met à jour l'interface tant que la PWA est
 * ouverte, le Push produit la notification système quand elle ne l'est plus.
 *
 * Sécurité — les invariants tenus ici :
 *
 *  - Le jeton part exclusivement dans l'en-tête `Authorization`, via l'instance axios partagée
 *    qui porte déjà le rafraîchissement Keycloak. Aucun `?token=`, aucun second mécanisme de
 *    rafraîchissement, aucun jeton stocké pour le Service Worker.
 *  - Le corps envoyé au backend ne contient AUCUNE identité : ni téléphone, ni identifiant de
 *    chauffeur. Le serveur déduit le propriétaire du `SecurityContext`. Un chauffeur ne peut
 *    donc pas enregistrer un appareil au nom d'un autre — il n'existe pas de champ pour le dire.
 *  - La clé publique VAPID vient du backend, source unique. La clé privée ne quitte jamais le
 *    serveur et n'apparaît nulle part dans ce fichier.
 */

const CHEMIN = '/chauffeur/push-subscriptions';

/** Le trio indispensable. Un seul manquant et le Push est hors de portée sur cet appareil. */
export const isPushSupported = (): boolean =>
    typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && 'PushManager' in window
    && 'Notification' in window;

/**
 * Convertit une clé VAPID base64url en `Uint8Array`, seule forme acceptée par
 * `PushManager.subscribe` comme `applicationServerKey`.
 *
 * `atob` n'accepte que le base64 standard : sans la retraduction de `-` et `_` et sans le
 * rembourrage, une clé sur deux environ échoue — et l'erreur du navigateur ne dit pas pourquoi.
 */
export const convertirCleVapid = (cleBase64Url: string): Uint8Array => {
    const rembourrage = '='.repeat((4 - (cleBase64Url.length % 4)) % 4);
    const base64 = (cleBase64Url + rembourrage).replace(/-/g, '+').replace(/_/g, '/');
    const binaire = window.atob(base64);
    const octets = new Uint8Array(binaire.length);
    for (let i = 0; i < binaire.length; i += 1) {
        octets[i] = binaire.charCodeAt(i);
    }
    return octets;
};

/** Clé publique VAPID, servie par le backend derrière authentification. */
export const getPublicVapidKey = async (): Promise<string> => {
    const reponse = await api.get(`${CHEMIN}/cle-publique`);
    const cle = reponse.data?.clePublique;
    if (!cle) {
        throw new Error('Clé publique VAPID indisponible.');
    }
    return cle;
};

/**
 * Abonne cet appareil et enregistre la souscription côté serveur.
 *
 * Réutilise la souscription existante quand le navigateur en a déjà une : la réenregistrer est
 * sans danger — le backend est idempotent sur (chauffeur, empreinte d'endpoint) — et cela remet
 * en base un appareil qui aurait été révoqué à tort.
 *
 * @returns true si l'appareil est abonné et enregistré
 */
export const subscribeToPush = async (): Promise<boolean> => {
    if (!isPushSupported()) {
        return false;
    }
    if (Notification.permission !== 'granted') {
        // Souscrire sans permission lève une exception sur la plupart des navigateurs. On sort
        // proprement : c'est à l'interface de demander la permission, jamais à ce service.
        return false;
    }

    const registration = await navigator.serviceWorker.ready;
    const existante = await registration.pushManager.getSubscription();
    const souscription = existante ?? await registration.pushManager.subscribe({
        // Obligatoire sur Chrome comme sur Safari : chaque Push reçu doit produire une
        // notification visible. Un Push silencieux ferait révoquer l'abonnement.
        userVisibleOnly: true,
        applicationServerKey: convertirCleVapid(await getPublicVapidKey()),
    });

    const donnees = souscription.toJSON();
    await api.post(CHEMIN, {
        endpoint: donnees.endpoint,
        keys: {
            p256dh: donnees.keys?.p256dh,
            auth: donnees.keys?.auth,
        },
    });
    return true;
};

/**
 * Désabonne cet appareil.
 *
 * Le serveur est prévenu d'abord : si l'ordre était inversé et que l'appel réseau échouait,
 * l'appareil serait désabonné localement mais resterait ciblé côté serveur, qui pousserait
 * indéfiniment vers un endpoint mort.
 */
export const unsubscribeFromPush = async (): Promise<boolean> => {
    if (!isPushSupported()) {
        return false;
    }
    const registration = await navigator.serviceWorker.ready;
    const souscription = await registration.pushManager.getSubscription();
    if (!souscription) {
        return false;
    }

    await api.delete(CHEMIN, { data: { endpoint: souscription.endpoint } });
    await souscription.unsubscribe();
    return true;
};
